/**
 * Доля хвоста публичного рендерера: вид `restore_purchases` (ссылка
 * «восстановить покупки»).
 *
 * WEB-2920, заход 3: перенесено из `PublicBlockRendererExtended.tsx` слово в
 * слово вместе со ссылкой (`RestorePurchasesLink`) и хуком перепроверки моста
 * (`useHasWeb2AppBridge` — экспортируется: его же зовёт доля
 * `CloseWebviewButtonRenderer.tsx`). Метка `case 'restore_purchases'` остаётся
 * в фасаде; ветка вида — функция отрисовки `renderRestorePurchases(ctx)`,
 * цвет текста этого блока приходит объектом-контекстом
 * (`ExtendedRenderContext`). Эту долю импортирует только фасад (и соседняя
 * доля): она едет в ленивом куске `extended`, а не в первом экране
 * (самопроверка обхода).
 */
import React, { useEffect, useState } from 'react';
import type { EditorBlock } from '@/store/editorSlice';
import { interpolate } from '@/player/interpolate';
import type { VariableContext } from '@/player/variable-context';
import { useEditorPreview } from '../EditorPreviewContext';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import { hasWeb2AppBridge, postToWeb2AppBridge } from '@/utils/web2appBridge';
import type { ExtendedRenderContext } from '../PublicBlockRendererExtended';

/**
 * WEB-1414 — «Восстановить покупки»: действующий элемент восстановления
 * (требование Apple 3.1.1), ссылка ВТОРИЧНОГО стиля (не CTA — служебная
 * подчёркнутая строка рядом с юр-ссылками). Контракт видимости:
 *  - внутри приложения (мост web2appBridge есть) — активна, тап шлёт мосту
 *    событие restore_purchases (SDK зовёт restorePurchases своего стора);
 *  - в обычном браузере моста нет — блок скрыт ВОВСЕ: восстановление покупок
 *    без приложения бессмысленно, мёртвая ссылка хуже пустоты (тот же
 *    принцип, что кнопка закрытия WEB-853);
 *  - на канве конструктора (isEditorPreview) — виден всегда, клик инертен,
 *    как у return_to_app_button (WEB-846).
 * Вынесен из switch отдельным компонентом: useEditorPreview — хук, в ветке
 * case его звать нельзя (та же причина, что у ReturnToAppButton выше).
 */

/**
 * Ревизия пачки 13: однократная проверка hasWeb2AppBridge() в теле рендера
 * навсегда прятала ссылку, если SDK инициализировал мост ПОСЛЕ первого
 * рендера страницы (у соседней кнопки закрытия есть свой таймер-перерендер,
 * у ссылки не было ничего). Одна перепроверка после монтирования: к этому
 * моменту нативный контейнер уже вставил объект моста в window.
 */
export function useHasWeb2AppBridge(): boolean {
  const [hasBridge, setHasBridge] = useState(() => hasWeb2AppBridge());
  useEffect(() => {
    if (hasWeb2AppBridge()) setHasBridge(true);
    // Однократная перепроверка после монтирования — намеренно без подписок.
     
  }, []);
  return hasBridge;
}

function RestorePurchasesLink({
  block,
  textColor,
  varCtx,
}: {
  block: EditorBlock;
  textColor?: string;
  varCtx?: VariableContext;
}): React.ReactElement | null {
  const { isEditorPreview } = useEditorPreview();
  const hasBridge = useHasWeb2AppBridge();
  if (!isEditorPreview && !hasBridge) return null;

  // Ревизия пачки 13 (п.4): текст — через ту же интерполяцию, что соседние
  // текстовые поля; фолбэк — из словаря языка воронки (varCtx.system.locale).
  const rawLabel =
    typeof block.buttonText === 'string' && block.buttonText.trim().length > 0
      ? block.buttonText
      : getPublicQuizStrings(varCtx?.system?.locale).restorePurchasesLabel;
  const label = varCtx ? interpolate(rawLabel, varCtx) : rawLabel;

  return (
    <div style={{ textAlign: 'center', marginBottom: 12 }}>
      <button
        type="button"
        data-testid={`restore-purchases-${block.id}`}
        onClick={() => {
          // В превью конструктора клик только выделяет блок (всплытие),
          // бридж-событий не шлём — боевой путь ниже.
          if (isEditorPreview) return;
          postToWeb2AppBridge({ event: 'restore_purchases' });
        }}
        style={{
          background: 'none',
          border: 'none',
          padding: 4,
          cursor: 'pointer',
          // WEB-1692 (репорт владельца 24.08): размер строки настраивается
          // общим полем блока; absent = прежние 13px до пикселя.
          fontSize: block.fontSize ?? 13,
          textDecoration: 'underline',
          color: textColor ?? 'var(--color-text-secondary, #8c8c8c)',
        }}
      >
        {label}
      </button>
    </div>
  );
}

/** Ветка `restore_purchases` фасада — тело `case` слово в слово. */
export function renderRestorePurchases(
  ctx: ExtendedRenderContext,
): React.ReactElement {
  const { block, varCtx, contentTextColor } = ctx;
  return (
    <RestorePurchasesLink
      block={block}
      // WEB-1702 п.2: у «Восстановления покупок» поле «Цвет текста внутри
      // элемента» живое (INNER_TEXT_COLOR_BLOCK_TYPES), и оно сильнее
      // общего цвета воронки, поэтому сюда идёт `contentTextColor`.
      // Ревизия пачки 2 (п.8) поправила вторую половину комментария: с
      // WEB-1696 у соседних кнопок (возврат в приложение, закрыть окно)
      // это поле ТОЖЕ есть — оба типа стоят в INNER_TEXT_COLOR_BLOCK_TYPES
      // и читают значение, но другим путём: не пропсом сверху, а сборщиком
      // стиля кнопки (`buttonSurfaceStyle` внутри самих компонентов,
      // WEB-1651-2). Разная проводка, одно и то же поле; общий цвет
      // воронки у них остаётся умолчанием, пока поле не задано.
      textColor={contentTextColor}
      varCtx={varCtx}
    />
  );
}
